/**
 * StandaloneQuickQuoteModal
 * Mobile-first, simplified quote creation.
 * Requires only customer name + phone + line items.
 * Bypasses the map calculator entirely.
 */
import React, { useState } from 'react';
import { X, Plus, Trash2, Send, CheckCircle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useCompany } from '../context/CompanyContext';

const emptyItem = () => ({ id: Date.now(), description: '', price: '' });

const StandaloneQuickQuoteModal = ({ open, onClose }) => {
  const { companyId } = useCompany();

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [sendSms, setSendSms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null); // { quoteId, name }

  const addItem = () => setItems(prev => [...prev, emptyItem()]);

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const updateItem = (id, field, value) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));

  const total = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);

  const reset = () => {
    setCustomerName('');
    setCustomerPhone('');
    setNotes('');
    setItems([emptyItem()]);
    setSendSms(true);
    setSent(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (sendSms && !customerPhone.trim()) {
      toast.error('Phone number is required to send via SMS');
      return;
    }
    const validItems = items.filter(i => i.description.trim() && parseFloat(i.price) >= 0);
    if (!validItems.length) {
      toast.error('Add at least one service item');
      return;
    }

    setSubmitting(true);
    try {
      const quotesRef = collection(db, 'companies', companyId, 'quotes');
      const quoteRef = doc(quotesRef);
      const quoteId = quoteRef.id;

      const lineItems = validItems.map(i => ({
        description: i.description.trim(),
        quantity: 1,
        unitPrice: parseFloat(i.price) || 0,
        total: parseFloat(i.price) || 0,
      }));

      await setDoc(quoteRef, {
        quoteId,
        companyId,
        status: 'draft',
        createdBy: auth.currentUser?.uid || '',
        client: {
          name: customerName.trim(),
          phone: customerPhone.trim(),
        },
        lineItems,
        notes: notes.trim(),
        total,
        totals: { subtotal: total, grandTotal: total },
        source: 'quick_quote',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sentAt: null,
        viewedAt: null,
      });

      if (sendSms && customerPhone.trim()) {
        const token = await auth.currentUser?.getIdToken();
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/quotes/${quoteId}/send-sms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ companyId }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'SMS send failed');
        }
        setSent({ quoteId, name: customerName.trim(), viaSms: true });
      } else {
        setSent({ quoteId, name: customerName.trim(), viaSms: false });
      }
    } catch (err) {
      console.error('[QuickQuote]', err);
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-full p-0 overflow-hidden">
        {sent ? (
          /* ── Success State ── */
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {sent.viaSms ? 'Quote Sent!' : 'Quote Created!'}
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              {sent.viaSms
                ? `${sent.name} will receive the quote via SMS.`
                : `Quote saved for ${sent.name}.`}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Done
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={reset}
              >
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Another
              </Button>
            </div>
          </div>
        ) : (
          /* ── Form State ── */
          <>
            <DialogHeader className="px-5 pt-5 pb-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg font-bold">Quick Quote</DialogTitle>
                <button
                  onClick={handleClose}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Send a price in under 60 seconds</p>
            </DialogHeader>

            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Customer */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Customer Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="John Smith"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="qq-customer-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Phone</Label>
                  <Input
                    placeholder="+1 555-000-0000"
                    type="tel"
                    value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="qq-customer-phone"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-gray-700">Services</Label>
                {items.map((item, idx) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Input
                      placeholder={`Service ${idx + 1}`}
                      value={item.description}
                      onChange={e => updateItem(item.id, 'description', e.target.value)}
                      className="h-9 text-sm flex-1"
                      data-testid={`qq-item-desc-${idx}`}
                    />
                    <div className="relative w-24">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        placeholder="0"
                        type="number"
                        min="0"
                        value={item.price}
                        onChange={e => updateItem(item.id, 'price', e.target.value)}
                        className="h-9 text-sm pl-6"
                        data-testid={`qq-item-price-${idx}`}
                      />
                    </div>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addItem}
                  className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-700 transition-colors mt-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add service
                </button>
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Notes (optional)</Label>
                <Textarea
                  placeholder="Any additional details..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="text-sm resize-none h-16"
                  data-testid="qq-notes"
                />
              </div>

              {/* Total */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm font-medium text-gray-700">Total</span>
                <span className="text-lg font-bold text-gray-900">
                  ${total.toFixed(2)}
                </span>
              </div>

              {/* Send via SMS toggle */}
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div
                  onClick={() => setSendSms(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${
                    sendSms ? 'bg-orange-500' : 'bg-gray-200'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      sendSms ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </div>
                <span className="text-sm text-gray-700">Send via SMS</span>
              </label>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="qq-submit-btn"
              >
                {submitting ? (
                  'Saving...'
                ) : sendSms ? (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Quote
                  </>
                ) : (
                  'Save Quote'
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StandaloneQuickQuoteModal;
